import { Suspense, forwardRef, lazy, useState } from 'react'

import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { faChartScatter, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AppBar, Button, Dialog, IconButton, Slide, Toolbar, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'
import theme from 'material/theme'
import { useSelector } from 'react-redux'

import {
  useCanSeePredictiveChances,
  useCanSeeScattergrams,
  useIsPredictiveChancesEnabledForHS,
  useIsScattergramsEnabledForHS,
} from 'store/hooks'
import { selectUser } from 'store/user'

// Loaded on demand: the modal body pulls in @scoir/student-profile and
// @scoir/scatterplot, which are only needed once the dialog is actually opened.
const AdmissionIntelligenceModalContent = lazy(() => import('./AdmissionIntelligenceModalContent'))

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    backgroundColor: theme.palette.pageBackground?.main,
  },
  appBar: {
    background: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    marginLeft: theme.spacing(2),
    color: theme.palette.common.black,
    flex: 1,
  },
}))

const AdmissionIntelligence = ({
  scid,
  displayViewText = false,
  displayText = false,
}: {
  scid: number
  displayViewText?: boolean
  displayText?: boolean
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(!open)

  const canSeePredictiveChances = useSelector(useCanSeePredictiveChances)
  const isPredictedChancesEnabledForHs = useSelector(useIsPredictiveChancesEnabledForHS)
  const canSeeScattergrams = useSelector(useCanSeeScattergrams)
  const isScattergramsEnabledForHs = useSelector(useIsScattergramsEnabledForHS)
  const user = useSelector(selectUser)

  const isAdmissionsIntelligenceDisabled =
    !canSeePredictiveChances && !canSeeScattergrams && !isPredictedChancesEnabledForHs && !isScattergramsEnabledForHs

  if (isAdmissionsIntelligenceDisabled) {
    return null
  }

  const iconColor = isPredictedChancesEnabledForHs ? theme.palette.secondary.dark : theme.palette.primary.main
  const icon = isPredictedChancesEnabledForHs ? faSparkles : faChartScatter
  const title = isPredictedChancesEnabledForHs ? 'Admission Intelligence' : 'Scattergram'
  const getDisabledReason = () => {
    if (!user?.HighSchoolStudentId) {
      // Student is not connected to a high school || public page
      return 'Connect with your high school to view Scattergrams and Predictive Chances.'
    } else if (!canSeePredictiveChances && !canSeeScattergrams) {
      // Student is not eligible to see acceptance chances AND/OR and scattergram YET
      return `${title} is not yet enabled for your class.`
    }
  }
  const isDisabled = Boolean(getDisabledReason())

  return (
    <>
      {displayViewText || displayText ? (
        <Tooltip
          title={getDisabledReason()}
          // Hide tooltip if no reason is provided
          open={isDisabled ? undefined : false}
        >
          <div>
            <Button
              color="primary"
              variant={displayText ? 'text' : 'outlined'}
              disabled={isDisabled}
              onClick={handleOpen}
              startIcon={
                <FontAwesomeIcon
                  icon={icon}
                  size="sm"
                  color={iconColor}
                />
              }
            >
              {`${displayViewText ? 'View ' : ''}${title}`}
            </Button>
          </div>
        </Tooltip>
      ) : (
        <Tooltip title={getDisabledReason() || title}>
          <div>
            <IconButton
              color="primary"
              onClick={handleOpen}
              aria-label={title}
              disabled={isDisabled}
            >
              <FontAwesomeIcon
                icon={icon}
                size="sm"
                color={isDisabled ? undefined : iconColor}
              />
            </IconButton>
          </div>
        </Tooltip>
      )}
      <Dialog
        fullScreen
        open={open}
        onClose={handleOpen}
        // @ts-expect-error - mui types are wrong
        TransitionComponent={Transition}
      >
        <AppBar
          className={classes.appBar}
          elevation={0}
        >
          <Toolbar>
            <Typography
              variant="h6"
              className={classes.title}
            >
              {title}
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleOpen}
              aria-label="close"
            >
              <FontAwesomeIcon
                fixedWidth
                color={theme.palette.grey[600]}
                size="xs"
                icon={faTimes}
              />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Toolbar />
        <Suspense fallback={null}>
          <AdmissionIntelligenceModalContent scid={scid} />
        </Suspense>
      </Dialog>
    </>
  )
}

export default AdmissionIntelligence

const Transition = forwardRef(function Transition(props, ref) {
  return (
    // @ts-expect-error - mui types are wrong
    <Slide
      direction="up"
      ref={ref}
      {...props}
    />
  )
})
